import {
  collection,
  query,
  orderBy,
  getDocs,
  where,
  doc,
  updateDoc,
} from "firebase/firestore";
import { db } from "../../firebase";
import { createFolder } from "../../services/FirestoreServices";

/**
 * handleCreateFolder
 * Reusable utility for creating a folder with default or custom subfolders.
 * @param {Object} params
 * @param {string} params.userId - The user's UID.
 * @param {string} params.folderName - The name of the folder to create.
 * @param {Array<string>} [params.subfolderNames] - Optional array of subfolder names.
 * @param {function} [params.onSuccess] - Optional callback on success.
 * @param {function} [params.onError] - Optional callback on error.
 * @returns {Promise<Object>} The created folder object.
 */

export async function handleCreateFolder({
  userId,
  folderName,
  subfolderNames,
  onSuccess,
  onError,
}) {
  const defaultSubfolders = [
    "Leadership and Governance",
    "Quality Assurance",
    "Research",
    "Teaching and Learning",
    "Student Services",
    "External Relations",
    "Resource Management",
    "Multi-Page Documents",
  ];

  try {
    // Check if folder with the same name already exists
    const foldersRef = collection(db, "users", userId, "folders");
    const q = query(foldersRef, where("folderName", "==", folderName));
    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      const error = new Error("Folder with that name already exists.");
      if (onError) onError(error);
      throw error;
    }

    const newFolder = await createFolder(
      userId,
      folderName,
      subfolderNames && subfolderNames.length > 0
        ? subfolderNames
        : defaultSubfolders
    );
    if (onSuccess) onSuccess(newFolder);
    return newFolder;
  } catch (error) {
    if (onError) onError(error);
    throw error;
  }
}

/**
 * handleRenameFolder
 * Renames a folder after checking if the new name already exists.
 * @param {Object} params
 * @param {string} params.userId - The user's UID.
 * @param {string} params.folderId - The ID of the folder to rename.
 * @param {string} params.newFolderName - The new folder name.
 * @param {function} [params.onSuccess] - Optional callback on success.
 * @param {function} [params.onError] - Optional callback on error.
 * @returns {Promise<void>}
 */
export async function handleRenameFolder({
  userId,
  folderId,
  newFolderName,
  onSuccess,
  onError,
}) {
  try {
    console.log("[DEBUG][USER][TITLE] folderUtils.js -> handleRenameFolder");
    console.log(
      "[DEBUG][USER] userId, folderId, newFolderName",
      userId,
      folderId,
      newFolderName
    );
    // Check if folder with the new name already exists
    const foldersRef = collection(db, "users", userId, "folders");
    const q = query(foldersRef, where("folderName", "==", newFolderName));
    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      const error = new Error("Folder with that name already exists.");
      if (onError) onError(error);
      throw error;
    }

    // Update folder name
    const folderDocRef = doc(db, "users", userId, "folders", folderId);
    await updateDoc(folderDocRef, { folderName: newFolderName });

    if (onSuccess) onSuccess();
  } catch (error) {
    if (onError) onError(error);
    throw error;
  }
}
